import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { API_BASE_URL } from '../tokens/api-base-url.token';

/**
 * Opt a request out of having the bearer token attached — for the login call
 * itself, or any request that must go out unauthenticated:
 *
 *   http.post(url, body, { context: new HttpContext().set(SKIP_AUTH, true) })
 */
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

/** Matches http://, https://, any scheme, and protocol-relative //host. */
const ABSOLUTE_URL = /^(?:[a-z][a-z\d+.-]*:)?\/\//i;

/**
 * Attaches `Authorization: Bearer <token>` to the app's own API calls.
 *
 * "Our own" means a relative path, or an absolute URL under API_BASE_URL.
 * Any other absolute URL is deliberately skipped: this page loads third-party
 * scripts, and a token must never be sent to a host that is not ours.
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH) || !isOurApi(req.url, inject(API_BASE_URL))) {
    return next(req);
  }

  const token = inject(AuthService).accessToken();
  if (!token) {
    return next(req);
  }

  return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
};

function isOurApi(url: string, apiBaseUrl: string): boolean {
  if (!ABSOLUTE_URL.test(url)) {
    return true;
  }
  const base = apiBaseUrl.replace(/\/+$/, '');
  // Boundary check so "…/api" does not also match "…/api-evil.example".
  return !!base && (url === base || url.startsWith(base + '/') || url.startsWith(base + '?'));
}
